import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Button,
	Col,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Row,
	UncontrolledTooltip,
} from "reactstrap";
import {
	saveWordPressIntegration,
	testWordPressConnection,
} from "../../../services/wordpressIntegrationApi";

function normalizeSiteUrl(value) {
	const trimmed = (value || "").trim();
	if (!trimmed) return "";
	if (/^https?:\/\//i.test(trimmed)) return trimmed;
	return `https://${trimmed}`;
}

function isValidHttpUrl(value) {
	try {
		const parsed = new URL(value);
		return parsed.protocol === "http:" || parsed.protocol === "https:";
	} catch {
		return false;
	}
}

export default function WordPressIntegrationModal({
	isOpen,
	onClose,
	websiteId,
	siteUrl: initialSiteUrl = "",
	onSaved,
}) {
	const [siteUrl, setSiteUrl] = useState("");
	const [username, setUsername] = useState("");
	const [appPassword, setAppPassword] = useState("");
	const [testedFingerprint, setTestedFingerprint] = useState(null);
	const [testLoading, setTestLoading] = useState(false);
	const [saveLoading, setSaveLoading] = useState(false);
	const [feedback, setFeedback] = useState(null);

	const credentialFingerprint = useMemo(
		() =>
			JSON.stringify({
				siteUrl: normalizeSiteUrl(siteUrl),
				username: username.trim(),
				appPassword: appPassword.trim(),
			}),
		[siteUrl, username, appPassword]
	);

	const credentialsTested = testedFingerprint === credentialFingerprint;

	useEffect(() => {
		if (!isOpen) return;
		setSiteUrl(normalizeSiteUrl(initialSiteUrl) || "");
		setFeedback(null);
		setTestedFingerprint(null);
		setTestLoading(false);
		setSaveLoading(false);
	}, [isOpen, initialSiteUrl]);

	function resetTestedState() {
		setTestedFingerprint(null);
		if (feedback?.type === "success") setFeedback(null);
	}

	function validateFields() {
		const normalizedUrl = normalizeSiteUrl(siteUrl);
		const user = username.trim();
		const password = appPassword.trim();
		if (!normalizedUrl || !user || !password) {
			return { ok: false, message: "Site URL, username, and application password are required." };
		}
		if (!isValidHttpUrl(normalizedUrl)) {
			return { ok: false, message: "Enter a valid WordPress site URL (e.g. https://example.com)." };
		}
		if (websiteId == null || websiteId === "") {
			return { ok: false, message: "No website selected." };
		}
		return { ok: true, siteUrl: normalizedUrl, username: user, appPassword: password };
	}

	async function handleTest(e) {
		e?.preventDefault?.();
		const validated = validateFields();
		if (!validated.ok) {
			setFeedback({ type: "danger", message: validated.message });
			return;
		}
		setTestLoading(true);
		setFeedback(null);
		try {
			const result = await testWordPressConnection({
				websiteId,
				siteUrl: validated.siteUrl,
				username: validated.username,
				appPassword: validated.appPassword,
			});
			if (!result.ok) {
				setTestedFingerprint(null);
				setFeedback({
					type: "danger",
					message: result.message || "WordPress connection test failed",
				});
				return;
			}
			setSiteUrl(validated.siteUrl);
			setTestedFingerprint(
				JSON.stringify({
					siteUrl: validated.siteUrl,
					username: validated.username,
					appPassword: validated.appPassword,
				})
			);
			setFeedback({
				type: "success",
				message: result.message || "WordPress connection successful",
			});
		} catch {
			setTestedFingerprint(null);
			setFeedback({ type: "danger", message: "WordPress connection test failed" });
		} finally {
			setTestLoading(false);
		}
	}

	async function handleSave(e) {
		e?.preventDefault?.();
		const validated = validateFields();
		if (!validated.ok) {
			setFeedback({ type: "danger", message: validated.message });
			return;
		}
		if (!credentialsTested) {
			setFeedback({
				type: "warning",
				message: "Test the connection successfully before saving.",
			});
			return;
		}
		setSaveLoading(true);
		setFeedback(null);
		try {
			const result = await saveWordPressIntegration({
				websiteId,
				siteUrl: validated.siteUrl,
				username: validated.username,
				appPassword: validated.appPassword,
			});
			if (!result.ok) {
				setFeedback({
					type: "danger",
					message: result.message || "Could not save WordPress integration",
				});
				return;
			}
			setAppPassword("");
			setTestedFingerprint(null);
			onSaved?.({
				siteUrl: result.data?.site_url || validated.siteUrl,
				username: result.data?.username || validated.username,
				integrationId: result.data?.id ?? null,
			});
			onClose?.();
		} catch {
			setFeedback({ type: "danger", message: "Could not save WordPress integration" });
		} finally {
			setSaveLoading(false);
		}
	}

	const busy = testLoading || saveLoading;

	return (
		<Modal
			isOpen={isOpen}
			toggle={busy ? undefined : onClose}
			size="lg"
			scrollable
			className="integration-modal"
			contentClassName="border-0 rounded"
			backdrop={busy ? "static" : true}
			keyboard={!busy}
		>
			<ModalHeader toggle={busy ? undefined : onClose} className="border-bottom">
				WordPress
			</ModalHeader>
			<ModalBody
				className="pt-4 px-4"
				style={{ maxHeight: "min(75vh, 640px)", overflowY: "auto", flexDirection: "column" }}
			>
				<p className="text-muted small mb-4 lh-base">
					Connect a WordPress site with an Application Password so this website can publish and sync
					content securely.
				</p>

				<section className="mb-4 pb-4 border-bottom">
					<div
						className="small fw-semibold text-uppercase text-muted mb-2"
						style={{ letterSpacing: "0.04em" }}
					>
						Status
					</div>
					{feedback ? (
						<Alert color={feedback.type} className="mb-0 py-2 small">
							{feedback.message}
						</Alert>
					) : credentialsTested ? (
						<Alert color="success" className="mb-0 py-2 small">
							Credentials verified - you can save this integration.
						</Alert>
					) : (
						<Alert color="light" className="mb-0 py-2 small text-dark border">
							Not configured - enter your username and application password, then test the
							connection.
						</Alert>
					)}
				</section>

				<section>
					<div
						className="small fw-semibold text-uppercase text-muted mb-2"
						style={{ letterSpacing: "0.04em" }}
					>
						WordPress credentials
					</div>
					<p className="text-muted small mb-3">
						<span id="wordpress-help-modal">
							Create an Application Password in WordPress (Users → Profile → Application Passwords)
							and paste it below.
						</span>
						<UncontrolledTooltip placement="right" target="wordpress-help-modal">
							Use a dedicated Application Password, not your main WordPress login password.
						</UncontrolledTooltip>
					</p>
					<form id="integration-wp-form" onSubmit={handleSave}>
						<Row className="g-3">
							<Col xs={12}>
								<Label for="wp-modal-site-url" className="small fw-semibold mb-1 d-block">
									Site URL
								</Label>
								<Input
									id="wp-modal-site-url"
									type="url"
									className="w-100"
									value={siteUrl}
									readOnly
									plaintext={false}
									autoComplete="off"
									disabled
									title="Taken from your connected website"
								/>
								<p className="text-muted small mb-0 mt-1">
									Uses the URL already on file for this website.
								</p>
							</Col>
							<Col xs={12}>
								<Label for="wp-modal-username" className="small fw-semibold mb-1 d-block">
									Username
								</Label>
								<Input
									id="wp-modal-username"
									type="text"
									className="w-100"
									value={username}
									onChange={(e) => {
										setUsername(e.target.value);
										resetTestedState();
									}}
									autoComplete="off"
									disabled={busy}
								/>
							</Col>
							<Col xs={12}>
								<Label for="wp-modal-app-password" className="small fw-semibold mb-1 d-block">
									Application Password
								</Label>
								<Input
									id="wp-modal-app-password"
									type="password"
									className="w-100"
									value={appPassword}
									onChange={(e) => {
										setAppPassword(e.target.value);
										resetTestedState();
									}}
									autoComplete="new-password"
									placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
									disabled={busy}
								/>
							</Col>
						</Row>
					</form>
				</section>
			</ModalBody>
			<ModalFooter className="border-top px-4 py-3 bg-light gap-2">
				<Button color="secondary" outline type="button" onClick={onClose} disabled={busy}>
					Cancel
				</Button>
				<Button
					color="outline-primary"
					type="button"
					onClick={handleTest}
					disabled={busy}
				>
					{testLoading ? "Testing…" : "Test connection"}
				</Button>
				<Button
					color="primary"
					type="submit"
					form="integration-wp-form"
					disabled={busy || !credentialsTested}
				>
					{saveLoading ? "Saving…" : "Save integration"}
				</Button>
			</ModalFooter>
		</Modal>
	);
}
